import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaArrowLeft, FaArrowRight, FaInfoCircle } from 'react-icons/fa';

interface ImageViewerProps {
  isOpen: boolean;
  onClose: () => void;
  image: {
    src: string;
    alt: string;
    title?: string;
    description?: string;
  } | null;
  onPrev?: () => void;
  onNext?: () => void;
  showNavigation?: boolean;
}

const ImageViewer: React.FC<ImageViewerProps> = ({
  isOpen,
  onClose,
  image,
  onPrev,
  onNext,
  showNavigation = false
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [showInfo, setShowInfo] = useState(true);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Détecter si l'appareil est mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fonction sécurisée pour fermer la visionneuse
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  // S'assurer que le bouton de fermeture est cliquable
  useEffect(() => {
    if (isOpen && closeButtonRef.current) {
      // Focus sur le bouton de fermeture pour s'assurer qu'il est accessible
      setTimeout(() => {
        if (closeButtonRef.current) {
          closeButtonRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen]);

  // Gestionnaire de balayage tactile pour mobile
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!image || !showNavigation || !onPrev || !onNext) return;
    
    const touchStartX = e.touches[0].clientX;
    
    const handleTouchMove = (moveEvent: TouchEvent) => {
      const touchEndX = moveEvent.touches[0].clientX;
      const diffX = touchStartX - touchEndX;
      
      // Détecter la direction du swipe
      if (Math.abs(diffX) > 50) { // Seuil pour considérer qu'il s'agit d'un swipe
        if (diffX > 0) {
          // Swipe gauche -> image suivante
          onNext();
        } else {
          // Swipe droit -> image précédente
          onPrev();
        }
        
        window.removeEventListener('touchmove', handleTouchMove);
      }
    };
    
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    
    // Nettoyer les écouteurs d'événements
    const handleTouchEnd = () => {
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
    
    window.addEventListener('touchend', handleTouchEnd, { once: true });
  }, [image, onNext, onPrev, showNavigation]);

  const toggleInfo = useCallback(() => {
    setShowInfo(prev => !prev);
  }, []);

  // Gestionnaire de clic sur l'arrière-plan
  const handleBackgroundClick = useCallback((e: React.MouseEvent) => {
    // Fermer seulement si on clique directement sur l'arrière-plan
    if (modalRef.current && e.target === modalRef.current) {
      handleClose();
    }
  }, [handleClose]);

  // Gestion des raccourcis clavier
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'Escape':
          handleClose();
          break;
        case 'ArrowLeft':
          if (onPrev && showNavigation) onPrev();
          break;
        case 'ArrowRight':
          if (onNext && showNavigation) onNext();
          break;
        case 'i':
          toggleInfo();
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    // Empêcher le défilement de la page quand la visionneuse est ouverte
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, handleClose, onPrev, onNext, showNavigation, toggleInfo]);

  // Si la visionneuse n'est pas ouverte ou qu'il n'y a pas d'image, ne rien rendre
  if (!isOpen || !image) {
    return null;
  }

  return (
    // Conteneur principal - niveau 0
    <div className="fixed inset-0 z-[9900] overflow-hidden">
      {/* Couche d'arrière-plan semi-transparente - niveau 1 */}
      <div 
        ref={modalRef}
        className="absolute inset-0 bg-black/95 flex items-center justify-center"
        onClick={handleBackgroundClick}
      >
        {/* Contenu de la visionneuse - niveau 2 */}
        <div className="relative w-full h-full flex items-center justify-center">
          {/* Image */}
          <div 
            className="w-full h-full flex items-center justify-center"
            onTouchStart={showNavigation ? handleTouchStart : undefined}
          >
            <img 
              src={image.src} 
              alt={image.alt} 
              className="max-w-full max-h-[85vh] object-contain px-2"
            />
          </div>
          
          {/* Informations sur l'image - adaptées pour mobile */}
          {(showInfo || !isMobile) && (image.title || image.description) && (
            <div 
              className="absolute bottom-0 left-0 right-0 bg-black/70 p-3 sm:p-4 text-white z-[9950]"
            >
              {image.title && <h3 className="text-base sm:text-lg font-medium">{image.title}</h3>}
              {image.description && <p className="text-xs sm:text-sm text-white/80 mt-1">{image.description}</p>}
            </div>
          )}
          
          {/* Boutons de navigation - sur les côtés - niveau 3 */}
          {showNavigation && (
            <>
              {onPrev && (
                <button 
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-white text-xl sm:text-2xl z-[9960] p-3 rounded-full bg-black/30 hover:bg-black/50 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPrev();
                  }}
                  aria-label="Image précédente"
                >
                  <FaArrowLeft />
                </button>
              )}
              
              {onNext && (
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-white text-xl sm:text-2xl z-[9960] p-3 rounded-full bg-black/30 hover:bg-black/50 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    onNext();
                  }}
                  aria-label="Image suivante"
                >
                  <FaArrowRight />
                </button>
              )}
            </>
          )}
        </div>
      </div>
        
      {/* Bouton de fermeture - au niveau le plus élevé (hors de tout autre conteneur) - niveau 4 */}
      <button 
        ref={closeButtonRef}
        className="absolute top-2 sm:top-4 right-2 sm:right-4 text-white text-xl sm:text-2xl z-[9999] p-4 rounded-full bg-black/50 hover:bg-black/70 transition-colors w-12 h-12 flex items-center justify-center"
        onClick={(e) => {
          e.stopPropagation();
          handleClose();
        }}
        aria-label="Fermer"
      >
        <FaTimes />
      </button>

      {/* Bouton info pour mobile - toggle les infos - niveau 4 également */}
      {isMobile && (image.title || image.description) && (
        <button
          className="absolute top-3 left-3 z-[9999] p-3 rounded-full bg-black/50 hover:bg-black/70 transition-colors w-10 h-10 flex items-center justify-center text-white"
          onClick={(e) => {
            e.stopPropagation();
            toggleInfo();
          }}
          aria-label="Afficher/masquer informations"
        >
          <FaInfoCircle size={20} />
        </button>
      )}

      {/* Indicateurs pour mobiles - montrer comment naviguer */}
      {showNavigation && isMobile && (
        <div className="absolute top-3 left-0 right-0 flex justify-center z-[9970]">
          <div className="bg-black/50 text-white text-xs px-3 py-1 rounded-full">
            Glissez pour naviguer
          </div>
        </div>
      )}

      {/* Gestes tactiles expliqués */}
      {isMobile && (
        <div className="absolute bottom-16 left-0 right-0 flex justify-center text-center px-4 z-[9970]">
          <div className="text-white/60 text-xs bg-black/30 px-3 py-1 rounded-full">
            Appuyez au centre pour {showInfo ? 'masquer' : 'afficher'} les détails
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageViewer; 